import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mainSiteUrl, categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
];

function resolveLangLabel(currentLang: string): string {
  const exact = languages.find((l) => l.code === currentLang);
  if (exact) return exact.label;
  const partial = languages.find((l) => currentLang.startsWith(l.code));
  return partial?.label ?? 'English';
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  const isHome = location.pathname === '/';

  const navLinks = [
    { label: t('nav.home'), to: '/', icon: 'ri-home-4-line', type: 'link' as const },
    { label: t('nav.shoppingGuide'), to: '/articles/basetao-spreadsheet/', icon: 'ri-shopping-bag-3-line', type: 'link' as const },
    { label: t('nav.categories'), to: '/#categories', icon: 'ri-grid-line', type: 'anchor' as const },
    { label: t('nav.faq'), to: '/#faq', icon: 'ri-question-line', type: 'anchor' as const },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b-2 border-background-200 bg-background-50">
      <nav className="mx-auto flex max-w-content items-center justify-between gap-4 px-4 md:px-6 h-16">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-line text-lg" />
          </span>
          <span className="font-heading text-base font-bold text-foreground-950 md:text-lg">
            Basetao Sheet Guide
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) =>
            link.type === 'link' ? (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                  link.to === '/articles/basetao-spreadsheet/'
                    ? 'bg-primary-100 text-primary-900 hover:bg-primary-200'
                    : 'text-foreground-900 hover:bg-background-100'
                }`}
              >
                <i className={`${link.icon} text-base`} />
                {link.label}
              </Link>
            ) : (
              <a
                key={link.to}
                href={isHome ? link.to.replace('/', '') : link.to}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <i className={`${link.icon} text-base`} />
                {link.label}
              </a>
            ),
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors"
              aria-label={t('nav.language')}
            >
              <i className="ri-global-line text-sm" />
              <span className="hidden sm:inline">
                {resolveLangLabel(i18n.language)}
              </span>
              <i className="ri-arrow-down-s-line text-xs" />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 min-w-[10rem] rounded-lg border border-background-200 bg-background-50 p-1 shadow-sm">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                      i18n.language === lang.code || i18n.language.startsWith(lang.code)
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-foreground-800 hover:bg-background-100'
                    }`}
                  >
                    {lang.label}
                    {(i18n.language === lang.code || i18n.language.startsWith(lang.code)) && (
                      <i className="ri-check-line text-sm" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
          >
            {t('nav.mainSite')}
            <i className="ri-external-link-line text-sm" />
          </a>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 md:hidden"
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) =>
              link.type === 'link' ? (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
                >
                  <i className={`${link.icon} text-base text-primary-600`} />
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.to}
                  href={link.to}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
                >
                  <i className={`${link.icon} text-base text-primary-600`} />
                  {link.label}
                </a>
              ),
            )}
          </div>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-1 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t('categories.title')}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.link}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-2 py-2 text-sm font-medium text-foreground-800 hover:border-primary-300"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md">
                    <img src={cat.image} alt={cat.name} className="h-full w-full object-cover" />
                  </span>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
          >
            {t('nav.mainSite')}
            <i className="ri-external-link-line text-sm" />
          </a>
        </div>
      )}
    </header>
  );
}